import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type {
  ReturnRequestShippingInfoDto,
  ShopLocationDto,
  ShopReturnToStoreStoresDto,
} from "@babackapp/private-api-client";
import type { ReactNode } from "react";
import { motion } from "motion/react";
import {
  resolveCarrierLabel,
  resolveReturnMethodKind,
  type ReturnMethodKind,
} from "../../utils/resolve-return-method";
import {
  CarrierMethodBody,
  StoreMethodBody,
  WarehouseMethodBody,
  type ReturnMethodBodyProps,
} from "./return-method-bodies";

type Props = {
  shippingInfo: ReturnRequestShippingInfoDto;
  returnStore?: ShopReturnToStoreStoresDto;
  location: ShopLocationDto | null;
};

const BODY_BY_KIND: Record<
  ReturnMethodKind,
  (props: ReturnMethodBodyProps) => ReactNode
> = {
  store: StoreMethodBody,
  warehouse: WarehouseMethodBody,
  carrier: CarrierMethodBody,
};

/** Nothing to say about the method yet: an unlabelled carrier, or a warehouse without address. */
const HAS_CONTENT_BY_KIND: Record<ReturnMethodKind, (props: ReturnMethodBodyProps) => boolean> = {
  store: () => true,
  warehouse: ({ location }) => !!location?.address,
  carrier: ({ carrier, carrierTitle }) => !!resolveCarrierLabel(carrierTitle, carrier),
};

export function ReturnMethodSection({ shippingInfo, returnStore, location }: Props) {
  const t = useCustomText();

  const kind = resolveReturnMethodKind(shippingInfo);
  const bodyProps: ReturnMethodBodyProps = {
    carrier: shippingInfo.carrier,
    carrierTitle: shippingInfo.carrierTitle,
    returnStore,
    location,
  };

  if (!HAS_CONTENT_BY_KIND[kind](bodyProps)) return null;

  const Body = BODY_BY_KIND[kind];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="flex flex-col gap-2 px-1"
    >
      <p className="text-sm font-medium text-foreground">{t("returnMode.title")}</p>
      <Body {...bodyProps} />
    </motion.div>
  );
}
